import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { projectService } from '../../../../services/projectService';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';

const ProjectForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    image: '',
    imageAlt: '',
    technologies: '',
    metricsUsers: '',
    metricsPerformance: '',
    metricsRating: '',
    link: '',
    status: 'active'
  });

  useEffect(() => {
    if (isEdit) {
      fetchProject();
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const data = await projectService?.getProjectById(id);
      setFormData({
        title: data?.title || '',
        category: data?.category || '',
        description: data?.description || '',
        image: data?.image || '',
        imageAlt: data?.imageAlt || '',
        technologies: data?.technologies?.join(', ') || '',
        metricsUsers: data?.metricsUsers || '',
        metricsPerformance: data?.metricsPerformance || '',
        metricsRating: data?.metricsRating || '',
        link: data?.link || '',
        status: data?.status || 'active'
      });
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        technologies: formData?.technologies?.split(',')?.map(t => t?.trim())?.filter(Boolean)
      };

      if (isEdit) {
        await projectService?.updateProject(id, submitData);
      } else {
        await projectService?.createProject(submitData);
      }
      navigate('/admin/dashboard/projects');
    } catch (err) {
      console.error('Error saving project:', err);
      setError(err?.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEdit ? 'Edit Project' : 'New Project'} - Admin Dashboard</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Link to="/admin/dashboard/projects">
                  <Button variant="ghost" size="sm" iconName="ArrowLeft" />
                </Link>
                <h1 className="text-lg md:text-xl font-bold text-foreground">
                  {isEdit ? 'Edit Project' : 'New Project'}
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-border p-6 md:p-8 space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-foreground mb-2">
                Title *
              </label>
              <Input
                id="title"
                name="title"
                type="text"
                placeholder="Enter project title"
                value={formData?.title}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-foreground mb-2">
                  Category *
                </label>
                <Input
                  id="category"
                  name="category"
                  type="text"
                  placeholder="e.g., Web Application"
                  value={formData?.category}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-foreground mb-2">
                  Status *
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData?.status}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Describe the project"
                value={formData?.description}
                onChange={handleChange}
                required
                disabled={loading}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>

            <div>
              <label htmlFor="technologies" className="block text-sm font-medium text-foreground mb-2">
                Technologies (comma-separated)
              </label>
              <Input
                id="technologies"
                name="technologies"
                type="text"
                placeholder="React, Node.js, MongoDB"
                value={formData?.technologies}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="image" className="block text-sm font-medium text-foreground mb-2">
                Image URL
              </label>
              <Input
                id="image"
                name="image"
                type="url"
                placeholder="https://example.com/image.jpg"
                value={formData?.image}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="imageAlt" className="block text-sm font-medium text-foreground mb-2">
                Image Alt Text
              </label>
              <Input
                id="imageAlt"
                name="imageAlt"
                type="text"
                placeholder="Describe the image"
                value={formData?.imageAlt}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="link" className="block text-sm font-medium text-foreground mb-2">
                Project Link
              </label>
              <Input
                id="link"
                name="link"
                type="url"
                placeholder="https://project-url.com"
                value={formData?.link}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="metricsUsers" className="block text-sm font-medium text-foreground mb-2">
                  Users Metric
                </label>
                <Input
                  id="metricsUsers"
                  name="metricsUsers"
                  type="text"
                  placeholder="e.g., 10K+"
                  value={formData?.metricsUsers}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="metricsPerformance" className="block text-sm font-medium text-foreground mb-2">
                  Performance Metric
                </label>
                <Input
                  id="metricsPerformance"
                  name="metricsPerformance"
                  type="text"
                  placeholder="e.g., 99.9%"
                  value={formData?.metricsPerformance}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="metricsRating" className="block text-sm font-medium text-foreground mb-2">
                  Rating Metric
                </label>
                <Input
                  id="metricsRating"
                  name="metricsRating"
                  type="text"
                  placeholder="e.g., 4.8/5"
                  value={formData?.metricsRating}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                iconName={loading ? 'Loader2' : 'Save'}
                iconPosition="left"
              >
                {loading ? 'Saving...' : isEdit ? 'Update Project' : 'Create Project'}
              </Button>
              <Link to="/admin/dashboard/projects">
                <Button type="button" variant="outline" disabled={loading}>
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </main>
      </div>
    </>
  );
};

export default ProjectForm;